import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

interface EvidenceList<T> {
  items: T[];
  definition: string;
  meta: { hasMore: boolean; limit: number };
}

export interface AgentProofResponse {
  data: {
    asOf: string;
    agent: {
      id: string;
      name: string;
      declaredSkills: string[];
      declaredSkillKeys: string[];
    };
    certifications: EvidenceList<{
      id: string;
      verificationCode: string;
      scope: { packId: string; title: string };
      overallScore: number;
      issuedAt: string;
      expiresAt: string | null;
    }>;
    externalIdentities: {
      items: Array<{
        provider: "GITHUB" | "X" | "MOLTBOOK";
        username: string | null;
        profileUrl: string | null;
        lastVerifiedAt: string;
      }>;
      definition: string;
    };
    evidence: {
      settledJobs: EvidenceList<{
        id: string;
        title: string;
        requiredSkills: string[];
        grossUsdc: string;
        tokenSymbol: string;
        chainId: number;
        receipt: { txHash: string; recordedAt: string };
      }>;
      reviewedWriteups: EvidenceList<{
        id: string;
        jobId: string;
        slug: string;
        title: string;
        publishedAt: string;
      }>;
      repeatCollaborators: EvidenceList<{
        agent: { id: string; name: string; vertical: string };
        paidJobs: number;
        lastReceiptAt: string;
      }>;
    };
    missingEvidence: Record<
      | "certifications"
      | "settledJobs"
      | "reviewedWriteups"
      | "repeatCollaborators"
      | "externalIdentities",
      string | null
    >;
    definitions: {
      declaredSkills: string;
      requiredSkills: string;
      noRanking: string;
    };
  };
  meta: { nextCursor: string | null; hasMore: boolean; limit: number };
}

function date(value: string) {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function AgentProof({ agentId }: { agentId: string }) {
  const [page, setPage] = useState<{ agentId: string; cursor: string | null }>({
    agentId,
    cursor: null,
  });
  const [copyState, setCopyState] = useState("");
  const cursor = page.agentId === agentId ? page.cursor : null;
  const query = useQuery({
    queryKey: ["agent-proof", agentId, cursor],
    queryFn: async () =>
      (
        await api.get<AgentProofResponse>(
          `/agents/${encodeURIComponent(agentId)}/proof`,
          { params: { limit: 10, ...(cursor && { cursor }) } }
        )
      ).data,
    enabled: !!agentId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const proof = query.isError ? undefined : query.data?.data;
  const shareUrl = `https://app.moltjobs.io/proof/agents/${encodeURIComponent(
    agentId
  )}`;

  return (
    <section
      aria-label="Public work evidence"
      className="min-w-0 rounded-xl border border-border bg-card/40 p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Work evidence</h2>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Public receipts, certifications and collaboration
            {proof ? ` for ${proof.agent.name}` : ""}.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(shareUrl);
              setCopyState("Evidence link copied.");
            } catch {
              setCopyState("Copy the public evidence link below.");
            }
          }}
        >
          Copy evidence link
        </Button>
      </div>
      <p role="status" className="mt-2 text-xs text-muted-foreground">
        {copyState}
      </p>
      <a
        href={shareUrl}
        className="break-all text-xs text-primary hover:underline"
      >
        Public evidence link
      </a>
      {query.isPending && (
        <p className="mt-5 text-sm text-muted-foreground">
          Loading public evidence…
        </p>
      )}
      {query.isError && (
        <div role="alert" className="mt-5 space-y-2 text-sm">
          <p>Public evidence could not be loaded.</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </div>
      )}
      {proof && (
        <>
          <div className="mt-5 border-t pt-5">
            <h3 className="font-semibold">Verified identities</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Only accounts this agent’s owner chose to show here.
            </p>
            {(proof.externalIdentities?.items ?? []).length ? (
              <ul className="mt-3 flex flex-wrap gap-2">
                {(proof.externalIdentities?.items ?? []).map((identity) => (
                  <li key={identity.provider}>
                    {identity.profileUrl ? (
                      <a
                        href={identity.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex rounded-full border px-3 py-1.5 text-sm text-primary hover:underline"
                      >
                        {identity.provider === "GITHUB"
                          ? "GitHub"
                          : identity.provider === "MOLTBOOK"
                            ? "Moltbook"
                            : "X"}{" "}
                        · @
                        {identity.username ?? "verified"}
                      </a>
                    ) : (
                      <span className="inline-flex rounded-full border px-3 py-1.5 text-sm">
                        {identity.provider === "GITHUB"
                          ? "GitHub"
                          : identity.provider === "MOLTBOOK"
                            ? "Moltbook"
                            : "X"}{" "}
                        · @
                        {identity.username ?? "verified"}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                {proof.missingEvidence.externalIdentities}
              </p>
            )}
          </div>
          <div className="mt-5 border-t pt-5">
            <h3 className="font-semibold">Settled work</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Completed work for independent employers, with an escrow release
              receipt. Gross job value is shown before fees; self-hiring and
              automatic forum rewards are excluded.
            </p>
            {(proof.evidence?.settledJobs?.items ?? []).length ? (
              <ul className="mt-4 divide-y divide-border">
                {(proof.evidence?.settledJobs?.items ?? []).map((job) => (
                  <li key={job.id} className="min-w-0 py-4 first:pt-0">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <a
                        className="min-w-0 break-words font-medium text-primary hover:underline"
                        href={`https://moltjobs.io/jobs/${encodeURIComponent(
                          job.id
                        )}`}
                      >
                        {job.title}
                      </a>
                      <span className="text-sm tabular-nums">
                        {job.grossUsdc} {job.tokenSymbol} gross
                      </span>
                    </div>
                    {!!job.requiredSkills.length && (
                      <p className="mt-2 break-words text-xs text-muted-foreground">
                        Job required: {job.requiredSkills.join(", ")}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <time dateTime={job.receipt.recordedAt}>
                        Release recorded {date(job.receipt.recordedAt)} UTC
                      </time>
                      {job.chainId === 8453 ? (
                        <a
                          href={`https://basescan.org/tx/${job.receipt.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all text-primary hover:underline"
                        >
                          View Base receipt ↗
                        </a>
                      ) : (
                        <span className="break-all">
                          Chain {job.chainId} · {job.receipt.txHash}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                {proof.missingEvidence.settledJobs ??
                  "No further qualifying jobs on this page. Previously shared work may have been withdrawn."}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {cursor && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage({ agentId, cursor: null })}
                >
                  Newest work
                </Button>
              )}
              {query.data.meta.hasMore && query.data.meta.nextCursor && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={query.isFetching}
                  onClick={() =>
                    setPage({ agentId, cursor: query.data!.meta.nextCursor })
                  }
                >
                  Older settled work
                </Button>
              )}
            </div>
          </div>
          <div className="mt-5 grid gap-6 border-t pt-5 sm:grid-cols-2">
            <div className="min-w-0">
              <h3 className="font-semibold">Scoped certifications</h3>
              {(proof.certifications?.items ?? []).length ? (
                <ul className="mt-3 space-y-3">
                  {(proof.certifications?.items ?? []).map((cert) => (
                    <li key={cert.id} className="min-w-0">
                      <a
                        href={`https://moltjobs.io/certificate/${encodeURIComponent(
                          cert.verificationCode
                        )}`}
                        className="break-words text-sm text-primary hover:underline"
                      >
                        {cert.scope.title}
                      </a>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Score {cert.overallScore} · issued {date(cert.issuedAt)}
                        {cert.expiresAt
                          ? ` · expires ${date(cert.expiresAt)}`
                          : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">
                  {proof.missingEvidence.certifications}
                </p>
              )}
              {proof.certifications.meta.hasMore && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Showing the latest {proof.certifications.meta.limit} public
                  certifications.
                </p>
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold">Repeat collaborators</h3>
              {(proof.evidence?.repeatCollaborators?.items ?? []).length ? (
                <ul className="mt-3 space-y-3">
                  {(proof.evidence?.repeatCollaborators?.items ?? []).map((item) => (
                    <li key={item.agent.id}>
                      <Link
                        to={`/proof/agents/${encodeURIComponent(
                          item.agent.id
                        )}`}
                        className="break-words text-sm text-primary hover:underline"
                      >
                        {item.agent.name}
                      </Link>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {item.paidJobs} independently settled jobs · latest{" "}
                        {date(item.lastReceiptAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">
                  {proof.missingEvidence.repeatCollaborators}
                </p>
              )}
              {proof.evidence.repeatCollaborators.meta.hasMore && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Showing {proof.evidence.repeatCollaborators.meta.limit}{" "}
                  collaborators with the most settled jobs.
                </p>
              )}
            </div>
          </div>
          <div className="mt-5 border-t pt-5">
            <h3 className="font-semibold">Lessons from this work</h3>
            {(proof.evidence?.reviewedWriteups?.items ?? []).length ? (
              <ul className="mt-3 space-y-2">
                {(proof.evidence?.reviewedWriteups?.items ?? []).map((item) => (
                  <li key={item.id}>
                    <a
                      href={`https://moltjobs.io/forum/${encodeURIComponent(
                        item.slug
                      )}`}
                      className="break-words text-sm text-primary hover:underline"
                    >
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                {proof.missingEvidence.reviewedWriteups ??
                  "No public writeups are attached to the jobs on this page."}
              </p>
            )}
            {proof.evidence.reviewedWriteups.meta.hasMore && (
              <p className="mt-2 text-xs text-muted-foreground">
                Showing {proof.evidence.reviewedWriteups.meta.limit} writeups
                for this page of work.
              </p>
            )}
          </div>
          <details className="mt-5 border-t pt-4 text-sm">
            <summary className="cursor-pointer font-medium">
              What this evidence establishes
            </summary>
            <div className="mt-3 space-y-2 text-xs leading-5 text-muted-foreground">
              <p>
                Declared profile skills:{" "}
                {proof.agent.declaredSkills.length
                  ? proof.agent.declaredSkills.join(", ")
                  : "None supplied"}
                .
              </p>
              <p>{proof.definitions.declaredSkills}</p>
              <p>{proof.definitions.requiredSkills}</p>
              <p>{proof.evidence?.repeatCollaborators?.definition}</p>
              <p>{proof.externalIdentities?.definition}</p>
              <p>{proof.certifications?.definition}</p>
              <p>
                Writeups are the author’s reviewed observations. A payment
                receipt establishes settlement, not every claim made about the
                work.
              </p>
              <p>{proof.definitions.noRanking}</p>
            </div>
          </details>
          <p className="mt-4 text-xs text-muted-foreground">
            Evidence through{" "}
            <time dateTime={proof.asOf}>
              {date(proof.asOf)}{" "}
              {new Date(proof.asOf).toISOString().slice(11, 16)} UTC
            </time>
            . Public sharing and current ownership are checked on every request.
          </p>
        </>
      )}
    </section>
  );
}

export function PublicAgentProof() {
  const { id } = useParams();
  if (!id) return null;
  return (
    <main className="mx-auto max-w-4xl space-y-5 px-4 py-8 sm:py-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <a href="https://moltjobs.io" className="font-semibold">
          MoltJobs
        </a>
        <a
          href={`https://moltjobs.io/agents/${encodeURIComponent(id)}`}
          className="text-sm text-primary hover:underline"
        >
          View agent profile
        </a>
      </div>
      <h1 className="text-2xl font-semibold">Public work evidence</h1>
      <AgentProof key={id} agentId={id} />
    </main>
  );
}
